<script setup>
import { useI18n } from 'vue-i18n';
import VEchart from 'vue-echarts';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/markLine';
import 'echarts/lib/component/dataZoom';
import 'echarts/lib/component/toolbox';
import NullWidget from '@/containers/widget/components/NullWidget';
import { debounce, cloneDeep } from 'lodash';
import moment from 'moment';
import {
  computed,
  ref,
  defineEmits,
  defineProps,
  watch,
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  defineOptions,
  inject
} from 'vue';

import ChartLastValue from '../ChartLastValue.vue';
import ChartTitle from '../ChartTitle.vue';

import {
  ON_EVENTS,
  useChartType,
  useDownload,
  useChartOptions,
  useEventTracker,
  useMonitorType,
  useTargetMapping,
  useChartAction
} from './definition.js';
import {
  useInterval,
  useStatisticalType,
  useDownloadChart,
  DEFAULT_STATISTIC
} from '../useChart.js';

defineOptions({
  name: 'InsightChart'
});

const { $util, $theme } = getCurrentInstance().appContext.config.globalProperties;
const { t } = useI18n();
const emit = defineEmits(Object.values(ON_EVENTS));
const props = defineProps({
  height: { type: Number, default: () => 220 },
  data: { type: Object, default: () => ({}) },
  collInterval: { type: String, default: () => '1m' },
  indivItemOptions: { type: Array, default: () => [] },
  inquiryTime: { type: Array, default: () => [] },
  title: { type: String, default: () => '' },
  closeAble: { type: Boolean, default: false },
  options: { type: Object, default: () => ({}) },
  paramDownloadChart: { type: Object, default: () => ({}) },
  showTooltipOfChart: { type: Boolean, default: true },
  isNow: { type: Boolean, default: false },
  showLastValue: { type: Boolean, default: () => true },
  refreshChartKey: { type: String, default: () => '' }
});

//
const hasChartDataFetched = ref(false);
const originalData = ref([]);
const displayData = ref([]);
const chartKey = ref('');
const chartRef = ref(null);
const chartSeries = ref([]);
const isEventDisabled = ref(false);
const selectedIndivItems = ref([]);
const displayTime = ref({ startTime: null, endTime: null });
const originalState = ref(null);
const chartContainerRef = ref(null);
const chartLegendState = ref({});
const originalInterval = ref(null);
const displayInterval = ref(null);
//
const { isLatestEvent, getEventKey, setEventKey, resetEvents } = useEventTracker();
const { interval, intervalOptions, getInterval, setInterval, getIntevalValue } = useInterval();
const { statisticalType, statisticalOptions, setDefaultStatType, isDefaultStatType } =
  useStatisticalType(props.options.productType);
const { chartType, chartTypeOptions, setChartType, setDefaultChartType, isDefaultChartType } =
  useChartType();
const { monitorType } = useMonitorType();
const {
  chartOptions,
  setLegendDisplay,
  setXAxisMinMax,
  setXAxisFormatter,
  setLegendStatus,
  setZoomBar,
  setZoomScale,
  lastValueArr,
  unitConfig,
  setAnimation,
  downloadExcelChart,
  setGridView
} = useChartOptions(
  chartSeries,
  chartType,
  {
    height: props.height,
    lineColor: $theme.COLOR.CHART,
    metricUnit: props.options.metricUnit,
    displayUnit: props.options.displayUnit,
    isEventDisabled,
    displayTime,
    originalInterval,
    displayInterval
  },
  props
);
const { chartStatus, broadcastActions } = useChartAction();
const { targetMapping } = useTargetMapping(originalData);
const { downloadOptions } = useDownload();
const { downloadPNGFile, downloadRawExcelFile } = useDownloadChart();
const isEvent = inject('isEvent');

const useState = (state) => state.value;
//
const isCDN = computed(() => props.options.productType.toLowerCase() === 'cdn');
const isShowAddWidget = computed(() => isEvent);
const isZoom = computed(() => {
  const { startTime, endTime } = displayTime.value;
  if (!startTime || !endTime) return false;
  const start = startTime > useState(originalState).startTime;
  const end = endTime < useState(originalState).endTime;
  return start || end;
});
const queryDuration = computed(() => {
  const startTime = displayTime.value.startTime;
  const endTime = displayTime.value.endTime;
  const duration = endTime - startTime;
  return duration;
});
const isCollValue = computed(() => {
  const product = props.options.productType.toLowerCase();
  if (product === 'cdn') return true;
  const minCollection = intervalOptions.value.find((e) => e.key === props.collInterval);
  const curInterval = intervalOptions.value.find((e) => e.key === useState(interval));
  if (!curInterval) return true;
  return curInterval.breakpoint <= minCollection.breakpoint;
});

const isOriginalState = computed(() => {
  const [oriState, disState] = [useState(originalState), useState(displayTime)];
  if (oriState.startTime != disState.startTime) return false;
  if (oriState.endTime != disState.endTime) return false;
  if (oriState.interval != useState(interval)) return false;
  return true;
});
const isShowLastValue = computed(() => {
  if (!props.showLastValue) return false;
  if (!useState(isChartHasData) || !useState(isChartWithDisplayData)) return false;
  if (isZoom.value) return false;

  if (!props.indivItemOptions.length) {
    return lastValueArr.value.length && statisticalType.value.length;
  } else {
    return (
      lastValueArr.value.length &&
      (useState(isCollValue) || statisticalType.value.length) &&
      selectedIndivItems.value.length
    );
  }
});

const isChartHasData = computed(() => {
  for (const item of useState(originalData)) {
    if (item.data.length) return true;
  }
  return false;
});

const isChartWithDisplayData = computed(() => {
  for (const item of useState(displayData)) {
    if (item.data.length) return true;
  }
  return false;
});

const checkDataForDownload = computed(() => useState(displayData).length > 0);
const checkDataForDownloadRaw = computed(() => useState(originalData).length > 0);
const isEmptyStatType = computed(() => !useState(isCollValue) && !useState(statisticalType).length);

const isInitialState = computed(() => {
  if (useState(isZoom)) return false;
  if (!useState(isOriginalState)) return false;
  if (!useState(isCollValue) && !useState(isDefaultStatType)) return false;
  if (!useState(isDefaultChartType)) return false;
  return true;
});

const updateChartKey = () => {
  nextTick(() => {
    broadcastActions(false);
    chartKey.value = new Date().getTime();
  });
};
const getQueryParams = () => {
  return {
    individualItems: useState(selectedIndivItems).map((e) => e.value),
    interval: useState(interval),
    statisticalType: isCollValue.value
      ? DEFAULT_STATISTIC
      : useState(statisticalType).map((e) => e.value),
    startTime: useState(displayTime).startTime,
    endTime: useState(displayTime).endTime
  };
};
const setOriginalState = () => {
  const [start, end] = props.inquiryTime;
  const [startTime, endTime] = [moment(start).valueOf(), moment(end).valueOf()];
  originalState.value = {
    startTime,
    endTime,
    interval: getInterval(endTime - startTime)
  };
  originalInterval.value = getIntevalValue(useState(originalState).interval);
  displayTime.value = { startTime, endTime };
  displayInterval.value = useState(originalInterval);
  setXAxisMinMax(startTime, endTime);
  setXAxisFormatter(useState(queryDuration));
  setIndivItem();
  setInterval(useState(queryDuration));
};

const setIndivItem = () => {
  selectedIndivItems.value = props.indivItemOptions.filter(({ isSelected }) => isSelected);
};
const onDataZoom = () => {
  const { dataZoom } = useState(chartRef).chart.getOption();
  const { startValue, endValue, start, end } = dataZoom[0];
  useState(displayTime).startTime = startValue;
  useState(displayTime).endTime = endValue;
  setZoomScale(start, end);
};

const buildEmptyView = () => {
  const { type, stack } = useState(chartType);
  setLegendDisplay(false);
  const newSeries = useState(originalData).map((el) => {
    return {
      ...el,
      data: [],
      mappedTarget: el.data,
      type,
      stack
    };
  });
  chartSeries.value = newSeries;
};

const buildChartData = (animation = true) => {
  setAnimation(animation);
  if (!useState(displayData).length) return buildEmptyView();
  const { type, stack } = useState(chartType);
  setLegendDisplay(true);
  const newSeries = useState(displayData).map((zoomEl) => {
    const mappedTarget = targetMapping(zoomEl);
    if (!mappedTarget)
      return {
        ...zoomEl,
        mappedTarget: [],
        // leftRemain: [],
        // rightRemain: [],
        type,
        stack
      };
    // const { startTime, endTime } = useState(displayTime);
    // const left = mappedTarget.filter((el) => el[0] < startTime);
    // const right = mappedTarget.filter((el) => el[0] > endTime);
    return {
      name: zoomEl.name,
      data: zoomEl.data,
      mappedTarget,
      // leftRemain: left,
      // rightRemain: right,
      type,
      stack
    };
  });
  chartSeries.value = newSeries;
};

const resetAllToDefault = () => {
  isEventDisabled.value = true;
  resetEvents();
  setGridView(false, 1);
  setZoomScale(0, 100);
  setLegendStatus({});
  const [start, end] = props.inquiryTime;
  const [startTime, endTime] = [moment(start).valueOf(), moment(end).valueOf()];
  originalState.value = {
    startTime,
    endTime,
    interval: getInterval(endTime - startTime)
  };
  displayTime.value = { startTime, endTime };
  setXAxisMinMax(startTime, endTime);
  setDefaultStatType();
  setDefaultChartType();
  nextTick(() => {
    isEventDisabled.value = false;
  });
};

const onUpdate = (isReset = false) => {
  const eventKey = setEventKey(ON_EVENTS.UPDATE);
  const { startTime, endTime, interval } = useState(originalState);
  emit(ON_EVENTS.CHANGE, {
    query: {
      ...getQueryParams(),
      startTime,
      endTime,
      interval
    },
    eventName: ON_EVENTS.UPDATE,
    eventKey: isReset ? null : eventKey
  });
};

const onChange = debounce((eventName = ON_EVENTS.CHANGE) => {
  if (useState(isEventDisabled)) return;
  const eventKey = setEventKey(ON_EVENTS.CHANGE);
  emit(ON_EVENTS.CHANGE, {
    query: getQueryParams(),
    eventName,
    eventKey
  });
}, 300);
const onReset = () => {
  isEventDisabled.value = true;
  resetEvents();
  setGridView(false, 1);
  setZoomScale(0, 100);
  const { startTime, endTime } = useState(originalState);
  displayTime.value = { startTime, endTime };
  setDefaultStatType();
  setDefaultChartType();
  nextTick(() => {
    onUpdate(true);
    isEventDisabled.value = false;
  });
};
const handleMonitoring = (monitor) => {
  if (monitor.type === 'policySetting') {
    emit(ON_EVENTS.ADD_EVENT_POLICY);
  }
  if (monitor.type === 'addWidget') {
    const { type, stack } = useState(chartType);
    const expChartType = type == 'line' && stack ? 'stack' : type;
    emit(ON_EVENTS.ADD_WIDGET, expChartType);
  }
};

const triggerCharZoom = () => {
  if (useState(chartStatus).zoom) return;
  const chartIns = useState(chartRef).chart;
  chartIns.dispatchAction({
    type: 'takeGlobalCursor',
    key: 'dataZoomSelect',
    dataZoomSelectActive: true
  });
};

const triggerChartActions = () => {
  const chartIns = useState(chartRef)?.chart;
  if (!chartIns) return;
  triggerCharZoom();
  broadcastActions(true);
};

const onLegendChange = ({ selected }) => {
  for (const legend in selected) {
    chartLegendState.value[legend] = selected[legend];
  }
  setLegendStatus(useState(chartLegendState));
};

const onChartResize = () => broadcastActions(false, 'zoom');

const onDownloadChart = (selItem) => {
  switch (selItem.type) {
    case 'image':
      downloadPNGFile({
        metric: props.data.data,
        mainChart: useState(chartContainerRef),
        hasData: useState(checkDataForDownload),
        downloadType: selItem.type
      });
      break;
    case 'excel': {
      const { startTime, endTime } = useState(displayTime);
      downloadExcelChart({
        data: cloneDeep(displayData.value).map((item) => {
          return {
            ...item,
            data: item.data.filter(
              ([time, value]) => startTime <= time && time <= endTime && value !== null
            )
          };
        }),
        dateOptions: useState(displayTime),
        hasData: useState(checkDataForDownload),
        downloadType: selItem.type
      });
      break;
    }
    default:
      downloadRawExcelFile({
        paramDownloadChart: props.paramDownloadChart,
        dateOption: props.inquiryTime,
        hasData: useState(checkDataForDownloadRaw),
        downloadType: selItem.type
      });
      break;
  }
};

const fillDummyValues = (data) => {
  if (!useState(originalState)) return data;
  const isSameInterval = useState(interval) === useState(originalState).interval;
  const { startTime, endTime } = useState(displayTime);
  const intervalVal = getIntevalValue(useState(interval));
  return data.map((el) => {
    const { data: chartData } = el;
    if (!chartData.length) return el;
    const filledData = [...chartData];

    if (!isSameInterval || chartData[0][0] - startTime > intervalVal)
      filledData.unshift([startTime, null]);

    if (!isSameInterval || endTime - chartData[chartData.length - 1][0] > intervalVal)
      filledData.push([endTime, null]);
    return {
      ...el,
      data: filledData
    };
  });
};

//Watchers
watch([() => props.inquiryTime[0], () => props.inquiryTime[1]], () => {
  resetAllToDefault();
});

watch(
  () => useState(chartOptions).series,
  () => updateChartKey(),
  { deep: true }
);

watch(
  () => props.refreshChartKey,
  () => updateChartKey(),
  { deep: true }
);

watch(
  () => props.data,
  ({ data, eventKey, eventName }) => {
    // const dataWithDummy = fillDummyValues(data);
    if (!eventKey || eventName === ON_EVENTS.UPDATE) {
      originalData.value = cloneDeep(data);
      if (!eventKey) displayData.value = cloneDeep(data);
      if (useState(isEmptyStatType)) displayData.value = [];
    } else if (useState(isEmptyStatType)) {
      displayData.value = [];
    } else displayData.value = cloneDeep(data);

    nextTick(() => {
      hasChartDataFetched.value = true;
    });
  },
  { deep: true, immediate: true }
);

watch(
  () => originalData,
  () => {
    if (!useState(originalState)) setOriginalState();
    else buildChartData(false);
  },
  { deep: true, immediate: true }
);

watch(
  () => displayData,
  () => {
    buildChartData(true);
  },
  { deep: true, immediate: true }
);

watch(
  () => props.indivItemOptions,
  () => {
    isEventDisabled.value = true;
    setIndivItem();
    nextTick(() => {
      isEventDisabled.value = false;
    });
  },
  { deep: true }
);

watch(
  () => selectedIndivItems,
  () => {
    if (useState(isEventDisabled)) return;
    emit(
      ON_EVENTS.INDIVIDUAL,
      useState(selectedIndivItems).map(({ value }) => value)
    );
    const oriState = useState(isOriginalState);
    if (!oriState) onChange();
    onUpdate(oriState);
  },
  { deep: true }
);

watch(interval, (val) => {
  if (useState(isEventDisabled)) return;
  displayInterval.value = getIntevalValue(useState(interval));
  emit(ON_EVENTS.INTERVAL, val);
  const oriState = useState(isOriginalState);
  if (!oriState) onChange();
  else onUpdate(true);
});

watch(
  () => statisticalType,
  ({ value: statTypes }) => {
    if (useState(isEventDisabled)) return;
    if (!statTypes.length) displayData.value = [];
    emit(
      ON_EVENTS.STATISTIC,
      statTypes.map(({ value }) => value)
    );
    const oriState = useState(isOriginalState);
    if (!oriState && statTypes.length) onChange();
    onUpdate(oriState);
  },
  { deep: true }
);

watch(
  () => displayTime,
  (val) => {
    const interval = getInterval(useState(queryDuration));
    displayInterval.value = getIntevalValue(interval);
    setGridView(useState(isZoom), 1);
    setZoomBar(isZoom.value);
    setInterval(useState(queryDuration));
    setXAxisFormatter(useState(queryDuration));
    if (useState(isEventDisabled)) return;
    emit(ON_EVENTS.ZOOM, val);
    onChange();
  },
  { deep: true }
);

onBeforeMount(() => {
  useState(chartRef)?.chart?.dispose();
});

const isChangeBolet = ref(false);
const changeHeightDownwrap = () => {
  isChangeBolet.value = !isChangeBolet.value;
};
</script>

<style scoped lang="scss" src="./style.scss"></style>

<template>
  <div
    ref="chartContainerRef"
    class="chart-container"
    :style="{
      height: isZoom ? '284px' : '240px',
      transition: `height ${isZoom ? 0 : 0.5}s ease`
    }"
  >
    <div class="chart-header">
      <div class="chart-title">
        <div class="chart-title_block">
          <img
            src="@/assets/img/scmp/icon_chart_up.png"
            alt="perf-icon"
            class="chart-title_block-icon"
          />
          <ChartTitle :maxWidth="indivItemOptions.length ? '200px' : '240px'" :data="options">
          </ChartTitle>
        </div>
        <div v-if="indivItemOptions.length" class="individual-items">
          <scpm-select-box
            v-model="selectedIndivItems"
            :mutateInput="$util.replaceFilterText"
            :items="indivItemOptions"
            :searchPlaceholder="t('searchLabel.searchLike', { VPC: t('label.individualItem') })"
            :selectedPlaceholder="t('label.selectIndividualItem')"
            :textAll="$t('label.allIndividualItems')"
            :messageErrorMaxSelect="
              t('message.selectCountErrorMessage', {
                max: 100,
                field: t('label.individualItem').toLowerCase()
              })
            "
            :dropdownErrorMessage="true"
            showOnlyText
            hasSearchField
            @changeHeightDownwrap="changeHeightDownwrap"
          />
        </div>
        <ChartLastValue
          class="last-value"
          v-if="isShowLastValue"
          :last-value-arr="lastValueArr"
          :unitConfig="unitConfig"
          :inquiry-time="inquiryTime"
          :chartRef="chartRef"
          :showTooltipOfChart="showTooltipOfChart"
          :isNow="isNow"
        />
      </div>
      <div class="chart-actions">
        <div class="left">
          <lego-button v-if="!isInitialState" primary @click="onReset" small>{{
            $t('button.reset')
          }}</lego-button>
          <div class="interval-selector">
            <lego-dropdown
              v-model="interval"
              :items="intervalOptions"
              itemValue="key"
              itemText="text"
            >
              <template v-slot:itemText="{ item }">
                <div
                  v-lego-tooltip="{ value: $t('label.intervalTime'), moreAction: 'clickToHide' }"
                >
                  {{ item.text }}
                </div>
              </template>
            </lego-dropdown>
          </div>
          <div v-if="isCollValue" class="collection-value">{{ t('label.collectionValue') }}</div>
          <div v-else class="statistical-selector">
            <scpm-select-box
              v-model="statisticalType"
              :items="statisticalOptions"
              :selectedPlaceholder="t('label.selectStaticTypes')"
              showOnlyText
              :tooltip="t('label.statiscalType')"
              :textAll="$t('label.allStaticTypes')"
            />
          </div>
        </div>
        <div class="right">
          <lego-drop-holder-item-icon
            :btnIconType="'line'"
            :btnIconName="chartType.iconName"
            :isDropIcon="true"
            :submenuIcon="true"
            :dropdownData="chartTypeOptions"
            :tooltip="$t('page.performance.graphType')"
            @click="setChartType"
          >
            <template #buttonTitle>{{ $t('page.performance.graphType') }}</template>
          </lego-drop-holder-item-icon>
          <lego-drop-holder-item-icon
            :btnIconType="'line'"
            :btnIconName="'download_small'"
            :isDropIcon="true"
            :submenuIcon="false"
            :dropdownData="downloadOptions"
            :tooltip="$t('page.performance.download')"
            @click="onDownloadChart"
          >
            <template #buttonTitle>{{ $t('page.performance.download') }}</template>
          </lego-drop-holder-item-icon>

          <template v-if="!isCDN">
            <div
              v-if="isShowAddWidget"
              class="btnAddWidget"
              v-lego-tooltip="$t('title.timeSeriesGraphAddWidget')"
            >
              <lego-icon
                :iconType="'line'"
                :icon="'add'"
                sm
                :size="18"
                @click="handleMonitoring({ type: 'addWidget' })"
              />
            </div>
            <lego-drop-holder-item-icon
              v-else
              ref="btnMonitor"
              :btnIconType="'line'"
              :btnIconName="'monitor_icon'"
              :isDropIcon="true"
              :submenuIcon="true"
              :dropdownData="monitorType"
              :tooltip="$t('page.performance.monitoring')"
              @click="handleMonitoring"
            >
              <template #buttonTitle>{{ $t('page.performance.monitoring') }}</template>
            </lego-drop-holder-item-icon>
          </template>

          <lego-icon
            v-if="closeAble"
            icon="close"
            :size="20"
            @click="emit(ON_EVENTS.CLOSE)"
            v-lego-tooltip="{
              value: t('page.performance.remove'),
              moreAction: 'clickToHide'
            }"
          />
        </div>
      </div>
    </div>
    <div class="chart-body">
      <template v-if="hasChartDataFetched">
        <v-echart
          v-if="isChartHasData"
          ref="chartRef"
          :key="chartKey"
          :autoresize="{ autoResize: onChartResize() }"
          :option="chartOptions"
          :style="{ height: `${isZoom ? '220px' : '176px'}` }"
          class="echart-cls"
          @legendselectchanged="onLegendChange"
          @rendered="triggerChartActions"
          @finished="triggerChartActions"
          @datazoom="onDataZoom"
        />
        <NullWidget v-else :style="`height: ${isZoom ? '220px !important' : '176px !important'}`" />
      </template>
    </div>
  </div>
  <!--  -->
  <div v-if="isChangeBolet" style="height: 0px; overflow: hidden"></div>
</template>
